import { type CreatorOfferingView, type CreatorPick, needsRange, pickDays } from '@/lib/creator'
import type { LineMoney } from '@/lib/order-money'
import { dayWord } from '@/lib/rental'
import { formatPLN } from '@/lib/tiers'
import { META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * The line's own number.
 *
 * "szac." is reserved for a figure the server will not stand behind — a room by
 * the hour, a crew day. Equipment hire is NOT one of them: the price of the
 * window is in the catalogue, so the amount shown is the amount charged and
 * what remains open is the TERM, which a person confirms.
 *
 * ── IT USED TO SELL, TOO ─────────────────────────────────────────────────
 * Three devices lived here: a green "pakiet −10% · oszczędzasz 74 zł" badge, a
 * "Jeszcze 2 rolki i zbijasz rabat do 15%" nudge with a one-click "Biorę 7"
 * button, and a rental upsell offering longer terms at a lower daily rate.
 * Every one of them was arithmetic off a `discount_ladder`, and the ladders are
 * gone — the only discount is a code an admin issues. What is left is the
 * price, which is now simply what the catalogue says.
 *
 * The rental TERM is still chosen, just not sold: the booking rail and the
 * creator both carry their own term picker.
 */
export function OrderLinePrice({
  offering,
  pick,
  money,
}: {
  offering: CreatorOfferingView
  pick: CreatorPick
  money: LineMoney
}) {
  const days = pickDays(offering, pick)

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {needsRange(offering) && days > 0 ? (
          <span className={cn(META, 'text-white/55')}>
            {days} {dayWord(days)}
          </span>
        ) : null}
      </div>
      <span className="text-[0.95rem] font-semibold text-white">
        {money.total > 0 ? (
          <>
            {formatPLN(money.total)}
            {money.quoteOnly ? (
              <span className="text-[0.75rem] font-medium text-white/55"> szac.</span>
            ) : null}
          </>
        ) : (
          <span className="text-[0.8rem] font-medium text-white/55">Wycena indywidualna</span>
        )}
      </span>
    </div>
  )
}
